/**
 * Typed accessor over `aiModelRegistry.json`.
 *
 * The JSON is the single source of truth for every AI model the tier system
 * may pick. This module wraps it with:
 *   - A frozen typed catalogue (`AI_MODEL_REGISTRY`)
 *   - Lookup helpers (`getModelSpec`, `isModelPromoEligible`)
 *   - Tier resolution (`getTierPeakModel`) that respects env overrides so a
 *     tier can be flipped at deploy time without editing the JSON.
 *
 * Precedence for tier peak selection:
 *   1. Env override — `VITE_AI_TIER_{PRO,MAX}_MODEL` (must be a valid registry id)
 *   2. `tierAssignments` from the JSON
 *   3. Fallback to Line's Flash if nothing valid resolved (defensive)
 *
 * See docs/pricing/line-tier-integration-plan.md Phase J.
 */

import registry from './aiModelRegistry.json'
import type { AiModelId, AiTierId } from '../types/ai'

export type AiModelProvider = 'google' | 'anthropic'
export type AiModelCapability = 'cheap' | 'reasoning' | 'premium'
export type VertexLocation = 'asia-south1' | 'global' | (string & {})

export interface AiModelSpec {
  id: AiModelId
  displayName: string
  provider: AiModelProvider
  vertexModelId: string
  vertexLocation: VertexLocation
  pricingUsdPerMillion: { input: number; output: number }
  gcpTrialEligible: boolean
  capability: AiModelCapability
  pricingVerified?: boolean
  notes?: string
}

interface TierAssignmentsWire {
  line: string
  line_pro: string
  line_max: string
}

interface RegistryWire {
  models: AiModelSpec[]
  tierAssignments: TierAssignmentsWire & Record<string, unknown>
}

const wire = registry as unknown as RegistryWire

const MODELS_BY_ID = new Map<string, AiModelSpec>()
for (const spec of wire.models) MODELS_BY_ID.set(spec.id, spec)

export const AI_MODEL_REGISTRY: readonly AiModelSpec[] = Object.freeze([...wire.models])

/** Read a model spec by id. Returns undefined for unknown ids. */
export const getModelSpec = (id: string | null | undefined): AiModelSpec | undefined => (
  id ? MODELS_BY_ID.get(id) : undefined
)

/** True when the model is covered by the GCP GenAI App Builder trial credit. */
export const isModelPromoEligible = (id: string | null | undefined): boolean => (
  getModelSpec(id)?.gcpTrialEligible ?? false
)

const ENV_OVERRIDE_KEY: Record<AiTierId, string> = {
  line: 'VITE_AI_TIER_LINE_MODEL',
  line_pro: 'VITE_AI_TIER_PRO_MODEL',
  line_max: 'VITE_AI_TIER_MAX_MODEL',
}

const readEnvOverride = (tier: AiTierId): string | undefined => {
  try {
    const raw = import.meta.env?.[ENV_OVERRIDE_KEY[tier]]
    if (typeof raw !== 'string') return undefined
    const trimmed = raw.trim()
    return trimmed.length > 0 ? trimmed : undefined
  } catch {
    return undefined
  }
}

const LINE_FALLBACK: AiModelId = 'gemini-2.5-flash'

/**
 * Resolves the concrete model id backing a tier. Every callsite in the router
 * / billing / catalogue funnels through here so switching tiers is a one-file
 * change (aiModelRegistry.json or an env var).
 */
export const getTierPeakModel = (tier: AiTierId): AiModelId => {
  const env = readEnvOverride(tier)
  if (env && MODELS_BY_ID.has(env)) return env as AiModelId
  const assigned = wire.tierAssignments[tier]
  if (typeof assigned === 'string' && MODELS_BY_ID.has(assigned)) return assigned as AiModelId
  // Defensive fallback — should never fire because the JSON always names a
  // valid model. If it does, use Line's Flash so the call still lands somewhere.
  return LINE_FALLBACK
}

/**
 * Sugar for surfaces that only need the DISPLAY name (chip tooltip, badge,
 * ledger row). Falls back to the id when the spec isn't in the registry —
 * important for historical turns persisted with a since-removed model id.
 */
export const getTierPeakDisplayName = (tier: AiTierId): string => {
  const id = getTierPeakModel(tier)
  return getModelSpec(id)?.displayName ?? id
}

/** Sugar for the ledger — which provider will get billed for a given tier's peak call. */
export const getTierPeakProvider = (tier: AiTierId): AiModelProvider => (
  getModelSpec(getTierPeakModel(tier))?.provider ?? 'google'
)
